// w36 Ertak — paper-cut tunnel-book fairy-tale wedding preview card.
//
// The full template is a deep-blue + gold tunnel-book invitation
// (castle silhouette, moon, butterflies). The catalog card keeps that
// mood: a navy paper-fold frame, an inner navy card, and a focal
// medallion holding a paper-cut castle silhouette under a gold moon —
// a tiny diorama. Names in the template font; eyebrow / footer use
// the standard small-caps treatment.
import { type LayoutCtx } from "./_shared";

const FRAME =
  "linear-gradient(155deg, #0a1a48 0%, #050d2a 55%, #1f3a8a 100%)";

export function ErtakPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const goldBright = "#e7c66b";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FRAME,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "9% 8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "6%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* paper-cut castle medallion */}
        <div style={{ width: "62%", aspectRatio: "1 / 1", position: "relative", marginBottom: "6%" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `1px solid ${accent}66`,
              overflow: "hidden",
              background: "linear-gradient(180deg, #0a1a48 0%, #1f3a8a 70%, #2a4ca5 100%)",
              display: "grid",
              placeItems: "end center",
            }}
          >
            <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden>
              {/* gold moon */}
              <circle cx="72" cy="28" r="11" fill={goldBright} opacity="0.92" />
              {/* faint star sparkles */}
              {[[18, 22], [42, 16], [86, 50], [12, 56], [30, 40]].map(([cx, cy], i) => (
                <circle key={i} cx={cx} cy={cy} r={i % 2 ? 0.9 : 1.4}
                  fill="#fff" opacity={i % 2 ? 0.7 : 0.9} />
              ))}
              {/* castle silhouette — paper-cut style, anchored to the floor */}
              <path
                d="M0 100 L0 78 L8 78 L8 70 L14 64 L20 70 L20 78 L28 78
                   L28 62 L32 58 L36 62 L36 50 L46 40 L52 34 L58 40
                   L68 50 L68 64 L72 64 L72 58 L78 52 L84 58 L84 78
                   L92 78 L92 70 L96 66 L100 70 L100 100 Z"
                fill={accent}
              />
              {/* hint of foreground grass / paper-cut detail */}
              <path
                d="M0 100 L0 92 L8 88 L16 92 L24 88 L34 94 L46 90 L58 96
                   L72 90 L84 94 L96 90 L100 94 L100 100 Z"
                fill="#0a1a48"
                opacity="0.85"
              />
            </svg>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 26, lineHeight: 1.12, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "5%",
          }}
        >
          {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.26em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
